import { Link } from 'react-router-dom';
import { ArrowRight, Coffee, Heart, Star, Users, Quote } from 'lucide-react';

const values = [
  {
    icon: Coffee,
    title: 'Organic Houseblend',
    description: 'Our signature organic houseblend coffee, carefully sourced and roasted to perfection. Every cup is crafted with love.'
  },
  {
    icon: Heart,
    title: 'Family Recipes',
    description: 'Our dishes are made with secret family recipes, blending Indian and Malaysian influences passed down through generations.'
  },
  {
    icon: Users,
    title: 'Khidmah with a Smile',
    description: 'We believe in the art of heartfelt service. Creating spaces where people connect, relax, and feel truly at home.'
  },
  {
    icon: Star,
    title: 'Fresh Daily',
    description: 'Our pastries are baked fresh daily, and our fusion dishes are prepared with the finest ingredients.'
  }
];

const customerReviews = [
  {
    text: "The best Nasi Lemak in the whole of East London. The sambal is unbeatable!",
    source: "Google Review"
  },
  {
    text: "Truly a gem of a cafe. Exceptional pastries, delicious hot drinks, and beautiful decor.",
    source: "Google Review"
  },
  {
    text: "Run by close friends who have poured their heart and soul into creating a warm and welcoming space.",
    source: "Google Review"
  }
];

const milestones = [
  { year: '2019', event: 'The idea was born' },
  { year: '2021', event: 'Our signature organic houseblend was created' },
  { year: '2023', event: 'Officially registered as a business' },
  { year: '2023', event: 'Found our perfect premises in Woodford Green' },
  { year: 'Jan 2024', event: 'Opened our doors to the community' },
  { year: '2024', event: 'Achieved 200+ 5-star Google reviews (ongoing)' },
];

export default function AboutPage() {
  return (
    <div className="min-h-screen pt-24" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#FAFAF5]">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center">
          <div
            
            
            
          >
            <p className="text-xs tracking-[0.2em] text-neutral-500 uppercase mb-4">Our Story</p>
            <h1 className="font-serif text-5xl md:text-6xl font-bold text-black mb-6">
              Welcome <span className="italic">Home</span>
            </h1>
            <p className="text-neutral-600 leading-relaxed text-lg mb-6">
              Honey I'm Home Coffee was born from a simple dream: to create a place where 
              exceptional coffee meets genuine warmth. A place where every guest feels like 
              they're coming home.
            </p>
            <p className="text-neutral-600 leading-relaxed text-lg">
              We're proud to serve our <strong>organic houseblend coffee</strong>, authentic 
              <strong> Malaysian fusion brunch</strong> made with our secret family recipes, 
              and <strong>freshly baked pastries</strong> every single day.
            </p>
          </div>
        </div>
      </section>

      {/* What Makes Us Special */}
      <section className="py-24 md:py-32 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div
              
               
              
            >
              <p className="text-xs tracking-[0.2em] text-neutral-500 uppercase mb-4">Our Heritage</p>
              <h2 className="font-serif text-4xl md:text-5xl font-semibold text-black mb-6">
                Indian & <span className="italic">Singaporean</span> Roots
              </h2>
              <p className="text-neutral-600 leading-relaxed mb-6">
                Honey I'm Home is a husband-and-wife, family-run business. With roots in North and South India, 
                and a Singaporean heritage shaped by time spent in Malaysia, we bring a unique blend of cultures 
                to everything we do. Our Malaysian-influenced dishes carry the warmth and flavours we grew up with.
              </p>
              <p className="text-neutral-600 leading-relaxed mb-6">
                Having travelled extensively, we've learned the true art of service — what we call <strong>Khidmah 
                with a smile</strong>. Coming from a holistic healing background, we understand that the right 
                environment, the right food, and engaging all the senses can aid in mental and emotional wellbeing.
              </p>
              <p className="text-neutral-600 leading-relaxed">
                This is why so many of our guests tell us they find our café relaxing and cosy — it's not by accident, 
                it's by design. Every element, from the aroma of our organic coffee to the care in our dishes, is 
                crafted to help you feel at home.
              </p>
            </div>
            
            <div
              
               
              
              className="space-y-6"
            >
              <div className="bg-[#FAFAF5] p-8">
                <h3 className="font-serif text-2xl font-semibold text-black mb-4">🍜 Fusion Dishes</h3>
                <p className="text-neutral-600 text-sm">
                  Made with <strong>our secret family recipes</strong> - blending Indian and Malaysian flavours 
                  passed down through generations. Our Nasi Lemak has been called "the best in East London."
                </p>
              </div>
              <div className="bg-[#FAFAF5] p-8">
                <h3 className="font-serif text-2xl font-semibold text-black mb-4">☕ Organic Coffee</h3>
                <p className="text-neutral-600 text-sm">
                  Our <strong>100% organic houseblend</strong> is carefully sourced and roasted to 
                  perfection. Crafted with love by our skilled baristas.
                </p>
              </div>
              <div className="bg-[#FAFAF5] p-8">
                <h3 className="font-serif text-2xl font-semibold text-black mb-4">🥐 Fresh Pastries</h3>
                <p className="text-neutral-600 text-sm">
                  <strong>Baked fresh daily</strong> - from buttery croissants to indulgent cinnamon 
                  rolls. Check what's available at the counter each morning.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Reviews */}
      <section className="py-24 bg-black text-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <div className="flex justify-center items-center gap-1 mb-4">
              {[1,2,3,4,5].map(i => (
                <Star key={i} size={24} className="text-yellow-400 fill-yellow-400" />
              ))}
            </div>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold">
              What Our <span className="italic">Guests Say</span>
            </h2>
            <p className="text-neutral-400 mt-4">5.0 Google Rating · Tripadvisor Travellers' Choice</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {customerReviews.map((review, index) => (
              <div
                key={index}
                
                 
                
                
                className="bg-white/5 p-8"
              >
                <Quote size={32} className="text-neutral-600 mb-4" />
                <p className="text-neutral-200 leading-relaxed mb-6 italic">
                  "{review.text}"
                </p>
                <span className="text-neutral-500 text-sm">{review.source}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="values-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <p className="text-xs tracking-[0.2em] text-neutral-500 uppercase mb-4">What We Stand For</p>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-black">
              Our <span className="italic">Values</span>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={value.title}
                
                 
                
                
                className="text-center p-8 bg-[#FAFAF5]"
                data-testid={`value-${index}`}
              >
                <value.icon size={32} className="mx-auto mb-6 text-black" strokeWidth={1} />
                <h3 className="font-serif text-xl font-semibold text-black mb-4">{value.title}</h3>
                <p className="text-neutral-600 text-sm leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-24 md:py-32 bg-[#FAFAF5]" data-testid="timeline-section">
        <div className="container mx-auto px-4 md:px-8 max-w-5xl">
          <div className="text-center mb-16">
            <p className="text-xs tracking-[0.2em] text-neutral-500 uppercase mb-4">Our Journey</p>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-black">
              Growing <span className="italic">Together</span>
            </h2>
          </div>
          
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div
                key={milestone.year}
                
                 
                
                className="flex items-center gap-8"
              >
                <div className="w-20 flex-shrink-0 text-right">
                  <span className="font-serif text-2xl font-bold text-black">{milestone.year}</span>
                </div>
                <div className="w-px h-16 bg-neutral-300 flex-shrink-0" />
                <div className="flex-1 bg-white p-6">
                  <p className="text-neutral-700">{milestone.event}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-black text-white">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center">
          <div
            
             
            
          >
            <h2 className="font-serif text-4xl md:text-5xl font-semibold mb-6">
              Come say <span className="italic">hello</span>
            </h2>
            <p className="text-neutral-400 mb-8 max-w-xl mx-auto">
              We'd love to welcome you to our café in Woodford Green. Stop by for our organic 
              coffee, stay for our Nasi Lemak.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/location"
                data-testid="about-find-us-btn"
                className="inline-flex items-center justify-center bg-white text-black px-8 py-4 text-xs tracking-widest uppercase font-bold hover:bg-neutral-200 transition-all"
              >
                Find Us
                <ArrowRight size={16} className="ml-2" />
              </Link>
              <Link
                to="/contact"
                data-testid="about-contact-btn"
                className="inline-flex items-center justify-center border border-white text-white px-8 py-4 text-xs tracking-widest uppercase font-bold hover:bg-white hover:text-black transition-all"
              >
                Get in Touch
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
